import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#setViewportDrawerState(false);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle(event) {
    // Own the summary interaction instead of letting the browser toggle the
    // <details> a second time after this component has already changed it.
    event?.preventDefault();
    return this.isOpen ? this.close() : this.open(undefined, event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Do not rely on the browser's deferred <summary> default action. On
    // touch devices, especially after a sticky header has changed state while
    // scrolling, that action can be cancelled and leave the drawer closed.
    // Mark the header wrapper explicitly before scroll-lock runs. Relying on
    // an ancestor :has([open]) selector is unreliable here because Horizon
    // applies `contain: style` to the open header. On iOS that leaves a
    // scrolled-away sticky header (and its fixed drawer) outside the viewport.
    this.#setViewportDrawerState(true);
    details.open = true;
    summary.setAttribute('aria-expanded', 'true');
    this.#resetDrawerScroll(details);

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      this.#resetDrawerScroll(details);

      // iOS applies the page scroll lock after the first frame and can copy
      // the document's scroll offset into the newly fixed drawer. Reset once
      // more after that hand-off so the menu always starts at its first item.
      requestAnimationFrame(() => this.#resetDrawerScroll(details));
      window.setTimeout(() => this.#resetDrawerScroll(details), 80);

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Always open the mobile navigation at its own top, independent of the
   * collection page's current scroll position or a previous drawer session.
   * @param {HTMLDetailsElement} details
   */
  #resetDrawerScroll(details) {
    const scrollContainers = details.querySelectorAll(
      '.menu-drawer, .menu-drawer__navigation-container, .menu-drawer__inner-submenu, .menu-drawer__navigation'
    );
    scrollContainers.forEach((element) => {
      if (element instanceof HTMLElement) element.scrollTop = 0;
    });
  }

  /**
   * Keeps the mobile drawer's containing header pinned to the visual viewport.
   * @param {boolean} isOpen
   */
  #setViewportDrawerState(isOpen) {
    const headerSection = this.closest('.header-section');
    headerSection?.classList.toggle('fenza-mobile-menu-open', isOpen);
    document.documentElement.classList.toggle('fenza-mobile-menu-open', isOpen);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        this.#resetDrawerScroll(details);
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          // Always return the mobile drawer to its initial state. Previously the
          // accordion details were excluded here, so the last opened + menu was
          // still expanded the next time the drawer opened.
          const openDetails = this.querySelectorAll('details[open]');
          openDetails.forEach(reset);
          this.#setViewportDrawerState(false);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
